import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Cliente, ClienteSimplificado, Funcionario, Page } from '../../../../models/cadastro.model';
import { ErrorModel } from '../../../../models/error.model';
import { Orcamento, OrcamentoDTO } from '../../../../models/financeiro.model';
import { Parametros } from '../../../../models/parametros.model';
import { CadastroService } from '../../../../services/cadastro.service';
import { FinanceiroService } from '../../../../services/financeiro.service';
import { MarmorariaAgendamento, MarmorariaService } from '../../../service/marmoraria.service';
import { ClienteModal } from '../../../../pages/modal/cliente-modal/cliente-modal';
import { OrcamentoModal } from '../../../../pages/modal/orcamento-modal/orcamento-modal';
import { NgxMaskDirective } from 'ngx-mask';
import { ParametrosService } from '../../../../services/parametros.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { toLocalISOString, toInputDateTime } from '../../../../core/utils/date.util';

@Component({
  selector: 'app-agendamento-manutencao-componnet',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ClienteModal, OrcamentoModal, NgxMaskDirective],
  templateUrl: './agendamento-manutencao.componnet.html',
  styleUrls: ['./agendamento-manutencao.componnet.css']
})
export class AgendamentoManutencaoComponnet implements OnInit {
  agendamentoForm: FormGroup;
  clienteSelecionado: ClienteSimplificado | null = null;
  orcamentoSelecionado: OrcamentoDTO | null = null;
  isClienteModalOpen = false;
  isOrcamentoModalOpen = false;
  isSubmitting = false;
  agendamentoId: number | null = null;
  isEditMode = false;

  /** Mínimo exigido pelo Validators.minLength da descrição. */
  readonly descricaoMinLength = 5;
  /** Signal (app é zoneless) alimentado pelo valueChanges para o contador de caracteres. */
  readonly descricaoLength = signal(0);

   periodoOptions: Parametros[] = [];
   complexidadeOptions: Parametros[] = [];
   tipoAgendamentoOptions: Parametros[] = [];
   funcionarios: Funcionario[] = [];

  constructor(
    private fb: FormBuilder,
    private marmorariaService: MarmorariaService,
    private cadastroService: CadastroService,
    private financeiroService: FinanceiroService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private parametrosService: ParametrosService,
  ) {
    this.agendamentoForm = this.createFormGroup();
    this.agendamentoForm.get('descricao')?.valueChanges.subscribe((valor: string | null) => {
      this.descricaoLength.set((valor ?? '').length);
    });
  }

  /** Quantos caracteres ainda faltam para a descrição atingir o mínimo exigido. */
  get descricaoCaracteresFaltantes(): number {
    return Math.max(this.descricaoMinLength - this.descricaoLength(), 0);
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.agendamentoId = idParam ? Number(idParam) : null;
    this.isEditMode = this.agendamentoId !== null && !Number.isNaN(this.agendamentoId);

    forkJoin({
      parametros: this.parametrosService.getParametrosByCategorias(['PERIODO', 'COMPLEXIDADE', 'TIPO_AGENDAMENTO']),
      funcionarios: this.cadastroService.getFuncionarios().pipe(catchError(() => of([]))),
      agendamento: this.isEditMode && this.agendamentoId
        ? this.marmorariaService.getAgendamentoById(this.agendamentoId)
        : of(null)
    }).subscribe({
      next: ({ parametros, funcionarios, agendamento }) => {
        this.periodoOptions = parametros.filter((p) => p.categoria === 'PERIODO');
        this.complexidadeOptions = parametros.filter((p) => p.categoria === 'COMPLEXIDADE');
        this.tipoAgendamentoOptions = parametros.filter((p) => p.categoria === 'TIPO_AGENDAMENTO');
        this.funcionarios = funcionarios;

        if (agendamento) {
          this.patchFormForEdit(agendamento);
          this.loadClienteSelecionado(agendamento.cliente.id);
          this.loadOrcamentoSelecionado(agendamento.idOrcamento);
        }
      },
      error: () => {
        this.toastr.error('Não foi possível carregar os dados do formulário.');
      }
    });

  }

  private createFormGroup(): FormGroup {
    return this.fb.group({
      tipoAgendamento: ['', [Validators.required]],
      dataAgendamento: ['', Validators.required],
      telefoneContato: ['', [Validators.required, Validators.minLength(10)]],
      enderecoAgendamento: ['', [Validators.required, Validators.minLength(5)]],
      complexidade: ['', [Validators.required]],
      periodoVisita: [''],
      idOrcamento: [null],
      idFuncionario: [null],
      dataConclusao: [null],
      descricao: ['', [Validators.required, Validators.minLength(5)]],
      nomeRazaoSocial: ['']
    });
  }

  private mapToOptions(params: Parametros[], categoria: string): any[] {
    return params
      .filter((parametro) => parametro.categoria === categoria)
      .map((parametro) => ({
        label: parametro.descricao,
        value: parametro.descricao
      }));
  }

  incluirCliente(): void {
    this.isClienteModalOpen = true;
  }

  closeClienteModal(cliente: any): void {
    this.isClienteModalOpen = false;
    if (cliente) {
      this.clienteSelecionado = cliente;
      this.agendamentoForm.patchValue({ nomeRazaoSocial: cliente.nomeRazaoSocial });
      this.preencherContatoDoCliente(cliente.id);
    }
  }

  /**
   * O modal devolve apenas ClienteSimplificado (sem endereco/telefone), por isso é
   * preciso buscar o cadastro completo. Os campos são apenas pré-preenchidos: o
   * usuário continua livre para editá-los, já que o agendamento pode ocorrer em
   * endereço diferente do cadastro.
   */
  private preencherContatoDoCliente(idCliente: number): void {
    if (!idCliente) {
      return;
    }

    this.cadastroService.getClienteById(idCliente).pipe(
      catchError(() => {
        this.toastr.warning('Não foi possível carregar endereço e telefone do cliente. Preencha manualmente.');
        return of(null);
      })
    ).subscribe((cliente: Cliente | null) => {
      if (!cliente) {
        return;
      }

      this.agendamentoForm.patchValue({
        enderecoAgendamento: this.montarEnderecoCompleto(cliente),
        telefoneContato: this.somenteDigitos(cliente.telefone) || this.somenteDigitos(cliente.telefone2)
      });
    });
  }

  /** Junta endereço, município e UF ignorando as partes vazias do cadastro. */
  private montarEnderecoCompleto(cliente: Cliente): string {
    const logradouro = (cliente.endereco ?? '').trim();
    const municipio = (cliente.municipio ?? '').trim();
    const uf = (cliente.uf ?? '').trim();

    const cidadeUf = [municipio, uf].filter((parte) => parte).join(' - ');
    return [logradouro, cidadeUf].filter((parte) => parte).join(', ');
  }

  /**
   * O campo usa ngx-mask ("(00) 00000-0000"), que espera receber apenas dígitos.
   * Telefones gravados já formatados no cadastro corromperiam a máscara.
   */
  private somenteDigitos(valor: string | null | undefined): string {
    return (valor ?? '').replace(/\D/g, '');
  }

  incluirOrcamento(): void {
    this.isOrcamentoModalOpen = true;
  }

  closeOrcamentoModal(orcamento: OrcamentoDTO | undefined): void {
    this.isOrcamentoModalOpen = false;
    if (orcamento) {
      this.orcamentoSelecionado = orcamento;
      this.agendamentoForm.patchValue({ idOrcamento: orcamento.id });
      this.agendamentoForm.get('idOrcamento')?.markAsTouched();
    }
  }

  save(): void {
    if (this.isSubmitting) {
      return;
    }

    const pendencias = this.listarPendencias();
    if (pendencias.length) {
      this.markFormGroupTouched(this.agendamentoForm);
      this.toastr.warning(
        `Não é possível salvar enquanto os campos obrigatórios não forem preenchidos: ${pendencias.join(', ')}.`,
        'Campos obrigatórios pendentes'
      );
      return;
    }

    if (this.agendamentoForm.invalid) {
      this.markFormGroupTouched(this.agendamentoForm);
      this.toastr.warning('Verifique os campos do formulário antes de salvar.');
      return;
    }

    this.isSubmitting = true;
    const cliente = this.clienteSelecionado!;
    const formValue = this.agendamentoForm.value;
    const payload: MarmorariaAgendamento = {
      id: this.isEditMode && this.agendamentoId ? this.agendamentoId : 0,
      tipoAgendamento: formValue.tipoAgendamento,
      complexidade: formValue.complexidade,
      dataAgendamento: toLocalISOString(formValue.dataAgendamento),
      cliente: {
        id: cliente.id,
        nomeRazaoSocial: cliente.nomeRazaoSocial,
        cpfCnpj: cliente.cpfCnpj,
        nomeFantasia: cliente.nomeFantasia,
        email: '',
        endereco: '',
        municipio: '',
        uf: '',
        pais: '',
        telefone:  '',
        telefone2: '',
        observacao: '',
        restricaoAtiva: false
      },
      enderecoAgendamento: formValue.enderecoAgendamento,
      telefoneContato: formValue.telefoneContato,
      dataConclusao: formValue.dataConclusao ? toLocalISOString(formValue.dataConclusao) : null,
      descricao: formValue.descricao,
      nomeRazaoSocial: formValue.nomeRazaoSocial,
      idOrcamento: formValue.idOrcamento ? Number(formValue.idOrcamento) : null,
      idFuncionario: formValue.idFuncionario ? Number(formValue.idFuncionario) : null,
      periodoVisita: formValue.periodoVisita?.trim() || null
    };

    console.log(payload);

    const request$ = this.isEditMode
      ? this.marmorariaService.updateAgendamento(payload)
      : this.marmorariaService.createAgendamento(payload);

    request$.subscribe({
      next: () => {
        this.router.navigate(['/home', 'agendamento'], {
          state: {
            successMessage: this.isEditMode
              ? 'Agendamento atualizado com sucesso.'
              : 'Agendamento criado com sucesso.'
          }
        });
      },
      error: (error) => {
        const apiError: ErrorModel | undefined = error?.error;
        const message = apiError?.message || 'Não foi possível salvar o agendamento.';
        this.toastr.error(message);
        this.isSubmitting = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/home', 'agendamento']);
  }

  /** Rótulos exibidos no toast, na mesma ordem em que os campos aparecem na tela. */
  private readonly rotulosObrigatorios: Record<string, string> = {
    tipoAgendamento: 'Tipo de Agendamento',
    dataAgendamento: 'Data do Agendamento',
    telefoneContato: 'Telefone de Contato',
    enderecoAgendamento: 'Endereço do Agendamento',
    complexidade: 'Complexidade',
    descricao: 'Descrição'
  };

  /**
   * Lista os campos obrigatórios ainda pendentes. O cliente entra separado porque
   * não é um controle do formulário, e sim uma seleção feita pelo modal.
   */
  private listarPendencias(): string[] {
    const pendencias: string[] = [];

    Object.entries(this.rotulosObrigatorios).forEach(([nome, rotulo]) => {
      if (this.agendamentoForm.get(nome)?.invalid) {
        pendencias.push(rotulo);
      }
    });

    if (!this.clienteSelecionado?.id) {
      pendencias.push('Cliente');
    }

    return pendencias;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private patchFormForEdit(agendamento: MarmorariaAgendamento): void {
    this.agendamentoForm.patchValue({
      tipoAgendamento: agendamento.tipoAgendamento || '',
      dataAgendamento: toInputDateTime(agendamento.dataAgendamento),
      telefoneContato: agendamento.telefoneContato || '',
      enderecoAgendamento: agendamento.enderecoAgendamento || '',
      complexidade: agendamento.complexidade || '',
      periodoVisita: agendamento.periodoVisita || '',
      idOrcamento: agendamento.idOrcamento,
      idFuncionario: agendamento.idFuncionario ?? null,
      dataConclusao: toInputDateTime(agendamento.dataConclusao),
      descricao: agendamento.descricao || '',
      nomeRazaoSocial: agendamento.nomeRazaoSocial || ''
    });
  }

  private loadClienteSelecionado(idCliente: number): void {
    if (!idCliente) {
      return;
    }

    this.cadastroService.getClienteById(idCliente).pipe(
      catchError(() => {
        this.clienteSelecionado = {
          id: idCliente,
          nomeRazaoSocial: `Cliente #${idCliente}`,
          cpfCnpj: '',
          nomeFantasia: ''
        };
        return of(null);
      })
    ).subscribe((cliente) => {
      if (cliente) {
        this.clienteSelecionado = {
          id: cliente.id,
          nomeRazaoSocial: cliente.nomeRazaoSocial,
          cpfCnpj: cliente.cpfCnpj,
          nomeFantasia: cliente.nomeFantasia
        };
      }
    });
  }

  private loadOrcamentoSelecionado(idOrcamento: number | null): void {
    if (!idOrcamento) {
      return;
    }

    this.financeiroService.getOrcamentoById(idOrcamento).pipe(
      catchError(() => {
        this.orcamentoSelecionado = {
          id: idOrcamento,
          idCliente: 0,
          idVendedor: 0,
          nomeCliente: `Orçamento #${idOrcamento}`,
          descontoAplicado: 0,
          valorTotal: 0,
          dataOrcamento: '',
          validadeOrcamento: '',
          diasEmAberto: 0
        };
        return of(null);
      })
    ).subscribe((orcamento: Orcamento | null) => {
      if (orcamento) {
        this.orcamentoSelecionado = {
          id: orcamento.id,
          idCliente: orcamento.cliente?.id || 0,
          idVendedor: orcamento.vendedor?.id || 0,
          nomeCliente: orcamento.cliente?.nomeRazaoSocial || `Orçamento #${orcamento.id}`,
          descontoAplicado: orcamento.descontoAplicado,
          valorTotal: orcamento.valorTotal,
          dataOrcamento: orcamento.dataOrcamento,
          validadeOrcamento: orcamento.validadeOrcamento,
          diasEmAberto: 0
        };
      }
    });
  }

}
